import { useEffect, useRef, useCallback } from 'react';
import { handleModalKeyNavigation, focusableIn } from '../ui/common/modalKeyNav';

export interface UseSheetDialogOptions {
  isOpen: boolean;
  onClose: () => void;
  /** CSS selector for the element that should receive focus when the sheet opens. */
  initialFocusSelector?: string;
}

/**
 * Shared focus-trap + Escape-to-close + initial-focus + focus-restore behavior for
 * compact "sheet" dialogs (ChatAside, InspectorAside). Built on the same pure
 * `handleModalKeyNavigation` helper the Modal component uses, so both surfaces trap
 * focus and close on Escape consistently.
 */
export function useSheetDialog<T extends HTMLElement>({
  isOpen,
  onClose,
  initialFocusSelector,
}: UseSheetDialogOptions): React.RefObject<T | null> {
  const containerRef = useRef<T | null>(null);
  const previousActiveElement = useRef<HTMLElement | null>(null);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      handleModalKeyNavigation(e, {
        isOpen,
        closeOnEscape: true,
        onClose,
        container: containerRef.current,
      });
    },
    [isOpen, onClose],
  );

  useEffect(() => {
    if (!isOpen) return;

    previousActiveElement.current = document.activeElement as HTMLElement;

    const timeoutId = window.setTimeout(() => {
      const container = containerRef.current;
      if (!container) return;
      const preferred = initialFocusSelector
        ? container.querySelector<HTMLElement>(initialFocusSelector)
        : null;
      const target = preferred ?? focusableIn(container)[0] ?? container;
      target.focus();
    }, 0);

    return () => {
      window.clearTimeout(timeoutId);
      const trigger = previousActiveElement.current;
      previousActiveElement.current = null;
      if (trigger?.isConnected) {
        trigger.focus();
      } else {
        // The element that opened the sheet was unmounted/removed while the
        // sheet was open (e.g. the triggering row was deleted). Falling
        // back to the container itself keeps focus somewhere visible and
        // sane instead of silently dropping it (or throwing on a detached
        // node).
        containerRef.current?.focus();
      }
    };
  }, [isOpen, initialFocusSelector]);

  useEffect(() => {
    if (!isOpen) return;
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleKeyDown]);

  return containerRef;
}
